import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { connect } from 'react-redux';

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import { Modal, Input, H3, H4, Text, Worksheet, Button, Panel, TableFigure, Table, Small, Box } from '@bigcommerce/big-design';
import { AddIcon, ArrowDropDownIcon, EditIcon, DeleteIcon } from '@bigcommerce/big-design-icons';

import { updateShipments, findPurchaseOrder } from '../../redux/actions/purchaseOrders';

import UpdateShipmentModal from './UpdateShipmentModal';
import AddShipmentModal from './AddShipmentModal';
import DeleteShipmentModal from './DeleteShipmentModal';

const UpdateShipments = ({ payload, sendUpdateShipments, bigcommerceSettings, loadPurchaseOrder, purchaseOrders }) => {
  const { id } = useParams();
  const [shipments, setShipments] = useState(null);
  const [showAddShipmentModal, setShowAddShipmentModal] = useState(false);
  const [showUpdateShipmentModal, setShowUpdateShipmentModal] = useState(false);
  const [showDeleteShipmentModal, setShowDeleteShipmentModal] = useState(false);

  const [activeShipment, setActiveShipment] = useState(null);

  useEffect(() => {
    loadPurchaseOrder(payload, id);
  }, []);

  const format = (value) => {
    let formattedValue = "";

    if(bigcommerceSettings.currency_symbol_location === 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }
    
    formattedValue += parseFloat(value).toFixed(bigcommerceSettings.decimal_places)

    if(bigcommerceSettings.currency_symbol_location !== 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }

    formattedValue = formattedValue.replace('.', bigcommerceSettings.decimal_separator)

    return formattedValue;
  }

  useEffect(() => {
    if(purchaseOrders.activePurchaseOrder && purchaseOrders.status === 'success') {
      setShipments(purchaseOrders.activePurchaseOrder.shipments.filter(n => n) || []);
    }
  }, [purchaseOrders]);

  const addShipment = () => {
    setShowAddShipmentModal(true);
  }

  const updateDialog = (shipmentId) => {
    setActiveShipment(shipments.find(shipment => shipment.id === shipmentId));
    setShowUpdateShipmentModal(true);
  }

  const deleteDialog = (shipmentId) => {
    setActiveShipment(shipments.find(shipment => shipment.id === shipmentId));
    setShowDeleteShipmentModal(true);
  }

  const postUpdate = () => {
    sendUpdateShipments(payload, purchaseOrder.id, shipmentsJson());
    setShowShipmentsModal(false);
  }

  const description = () => {
    return(
      <> 
        <Text>Manage shipment information for this purchase order.</Text>
        <Text>This information will be included in purchase order PDFs on a separate shipping page under the original purchase order.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  if(!purchaseOrders.activePurchaseOrder) {
    return(
      <>
        <PageHeader stripeBackground='#c66628' helpLink="bigcommerce/update-purchase-order-shipments/" />
        <SettingsPage title="Purchase Order Shipments" description={description()}>
          <Panel>
            <H4>Purchase Order Shipments</H4>
            <LoadingSpinner statusText="Loading Purchase Order"/>
          </Panel>
        </SettingsPage>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#c66628' helpLink="bigcommerce/update-purchase-order-shipments/" />
      <SettingsPage title="Purchase Order Shipments" description={description()}>
        <Panel>
          <H4>Purchase Order Shipments</H4>
            { (shipments && shipments.length > 0) &&
              <TableFigure>
                <Table
                  columns={[
                      { header: 'Shipping company', hash: 'company_name', render: ({ company_name }) => company_name },
                      { header: 'Tracking #', hash: 'tracking', render: ({ tracking }) => tracking },
                      { header: 'Taxes and duties', hash: 'taxes', render: ({ taxes }) => taxes },
                      { header: 'Other', hash: 'other', render: ({ other }) => other },
                      { header: 'Actions', hash: 'stock', render: ({ id }) => 
                        <>
                          <span className="left-15 bottom-15"><Button iconLeft={<EditIcon />} variant="secondary" onClick={() => updateDialog(id)}>Edit</Button></span>
                          <span className="left-15 bottom-15"><Button iconLeft={<DeleteIcon />} variant="secondary" actionType="destructive" onClick={() => deleteDialog(id)}>Delete</Button></span>
                        </> },
                    ]}
                    items={shipments}
                  >
                </Table>
              <Button iconLeft={<AddIcon />} onClick={() => addShipment()}>New Shipment</Button>
            </TableFigure>
          }



          { (!shipments || shipments.length === 0) &&
            <Box
              backgroundColor="warning10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              <div className="text-center">
                <Text>No shipments created yet for this purchase order. Create shipments to see and manage them here.</Text>
                <Button iconLeft={<AddIcon />} onClick={() => addShipment()}>New Shipment</Button>
              </div>
            </Box>
          }
        </Panel>
      </SettingsPage>

      { showAddShipmentModal && 
        <AddShipmentModal
          payload={payload}
          showAddShipmentModal={showAddShipmentModal}
          setShowAddShipmentModal={setShowAddShipmentModal}
          postUpdate={postUpdate}
          shipments={shipments}
          setShipments={setShipments}
          purchaseOrderId={id}
        />
      }

      { showUpdateShipmentModal && 
        <UpdateShipmentModal
          payload={payload}
          showUpdateShipmentModal={showUpdateShipmentModal}
          setShowUpdateShipmentModal={setShowUpdateShipmentModal}
          shipment={activeShipment}
          postUpdate={postUpdate}
          shipments={shipments}
          setShipments={setShipments}
          purchaseOrderId={id}
        />
      }

      { showDeleteShipmentModal &&
        <DeleteShipmentModal
          payload={payload}
          showDeleteShipmentModal={showDeleteShipmentModal}
          setShowDeleteShipmentModal={setShowDeleteShipmentModal}
          shipment={activeShipment}
          postUpdate={postUpdate}
          shipments={shipments}
          setShipments={setShipments}
          purchaseOrderId={id}
        />
      }
    </>
  )

}

const mapStateToProps = state => ({
  bigcommerceSettings: state.stores.settings,
  purchaseOrders: state.purchaseOrders,
});

const mapDispatchToProps = (dispatch) => ({
  loadPurchaseOrder: (payload, id) => dispatch(findPurchaseOrder(payload, id, null)),
  // sendUpdateShipments: (payload, purchaseOrderId, shipments) => dispatch(markPORejected(payload, purchaseOrderId, shipments)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdateShipments);